import React, { useState, useMemo, useEffect } from 'react'
import { logError } from '@utils/logger'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  StarIcon,
  DocumentTextIcon,
  ClockIcon,
  TrashIcon,
  PencilIcon,
  EllipsisVerticalIcon,
  ShareIcon
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import toast from 'react-hot-toast'

import { useDocumentStore } from '@stores/documentStore'
import { useAuthStore } from '@stores/authStore'
import { documentsAPI } from '@api/documents'
import type { Document } from '../../types'
import { formatDistanceToNow } from 'date-fns'
import ShareDocumentModal from '@components/document/ShareDocumentModal'
import { CreateDocumentModal } from '@components/document/CreateDocumentModal'
import ConfirmDialog from '@components/common/ConfirmDialog'
import TagEditor from '@components/document/TagEditor'

type ViewFilter = 'all' | 'recent' | 'favorites'
type SortOption = 'updated' | 'created' | 'title' | 'size'

const DocumentsPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { createDocument, deleteDocument } = useDocumentStore()
  
  // Read filter from URL query params
  const filterParam = searchParams.get('filter')
  const initialFilter: ViewFilter = filterParam === 'starred' ? 'favorites' : filterParam === 'recent' ? 'recent' : 'all'
  
  const [searchQuery, setSearchQuery] = useState('')
  const [viewFilter, setViewFilter] = useState<ViewFilter>(initialFilter)
  const [sortBy, setSortBy] = useState<SortOption>('updated')
  
  // Sync filter with URL params
  useEffect(() => {
    const filterParam = searchParams.get('filter')
    if (filterParam === 'starred') {
      setViewFilter('favorites')
    } else if (filterParam === 'recent') {
      setViewFilter('recent')
    } else if (!filterParam) {
      setViewFilter('all')
    }
  }, [searchParams])
  
  // Update URL when filter changes
  const handleFilterChange = (filter: ViewFilter) => {
    setViewFilter(filter)
    if (filter === 'favorites') {
      setSearchParams({ filter: 'starred' })
    } else if (filter === 'recent') {
      setSearchParams({ filter: 'recent' })
    } else {
      setSearchParams({})
    }
  }
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set())
  const [showBulkActions, setShowBulkActions] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [selectedDocumentForShare, setSelectedDocumentForShare] = useState<string | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<Document | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [editingTagsFor, setEditingTagsFor] = useState<string | null>(null)
  
  // Fix: Add modal at the end of component

  // Fetch documents based on filter
  // Only fetch if user is authenticated (this page is protected, but double-check)
  const { isAuthenticated } = useAuthStore()
  const { data: allDocuments = [], isLoading: isLoadingAll } = useQuery({
    queryKey: ['documents'],
    queryFn: () => documentsAPI.getAll(),
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated, // Only fetch when authenticated
  })

  const { data: recentDocuments = [], isLoading: isLoadingRecent } = useQuery({
    queryKey: ['documents', 'recent'],
    queryFn: () => documentsAPI.getRecent(20),
    staleTime: 2 * 60 * 1000,
    enabled: isAuthenticated && viewFilter === 'recent' // Only fetch when authenticated and filter is 'recent'
  })

  const { data: favoriteDocuments = [], isLoading: isLoadingFavorites } = useQuery({
    queryKey: ['documents', 'favorites'],
    queryFn: documentsAPI.getFavorites,
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated && viewFilter === 'favorites' // Only fetch when authenticated and filter is 'favorites'
  })

  // Get current documents based on filter
  const currentDocuments = useMemo(() => {
    switch (viewFilter) {
      case 'recent':
        return recentDocuments
      case 'favorites':
        return favoriteDocuments
      default:
        return allDocuments
    }
  }, [viewFilter, allDocuments, recentDocuments, favoriteDocuments])

  // Filter and sort documents
  const filteredDocuments = useMemo(() => {
    let filtered = currentDocuments

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(doc => 
        doc.title.toLowerCase().includes(query) ||
        doc.content.toLowerCase().includes(query)
      )
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'title':
          return a.title.localeCompare(b.title)
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        case 'size':
          return b.character_count - a.character_count
        case 'updated':
        default:
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      }
    })

    return filtered
  }, [currentDocuments, searchQuery, sortBy])

  const isLoading = isLoadingAll || isLoadingRecent || isLoadingFavorites

  // Handlers
  const handleCreateDocument = async (
    title: string, 
    documentType: string, 
    content?: string,
    skipTemplate?: boolean
  ): Promise<string> => {
    try {
      // For LaTeX documents, backend will automatically create main.tex
      // Just pass the content/template to backend
      const documentData: any = {
        title,
        content: content || '',
        document_type: documentType as any,
      }
      
      // Only include skip_template if it's explicitly true (for ZIP uploads)
      // If false or undefined, don't include it (backend defaults to false)
      if (skipTemplate === true) {
        documentData.skip_template = true
      }
      
      const newDoc = await createDocument(documentData)
      
      // Don't navigate here - let CreateDocumentModal handle navigation
      // This allows the modal to stay open during ZIP uploads
      return newDoc.id
    } catch (error) {
      logError('Failed to create document:', error)
      throw error // Re-throw to let modal handle error display
    }
  }

  const handleOpenDocument = async (document: Document) => {
    try {
      // Mark as opened for recent tracking
      await documentsAPI.markOpened(document.id)
      navigate(`/editor/${document.id}`)
    } catch (error) {
      // Still navigate even if marking failed
      navigate(`/editor/${document.id}`)
    }
  }

  const handleToggleFavorite = async (document: Document, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await documentsAPI.toggleFavorite(document.id)
      // Invalidate queries to refresh data
      window.location.reload() // Simple refresh for now
    } catch (error) {
      logError('Failed to toggle favorite:', error)
    }
  }

  const handleDeleteDocument = (document: Document, e: React.MouseEvent) => {
    e.stopPropagation()
    setDocumentToDelete(document)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!documentToDelete) return
    
    setIsDeleting(true)
    try {
      await deleteDocument(documentToDelete.id)
      // Don't show toast here - documentStore already shows it
      setDeleteConfirmOpen(false)
      setDocumentToDelete(null)
    } catch (error) {
      logError('Failed to delete document:', error)
      // Don't show toast here - documentStore already shows it
    } finally {
      setIsDeleting(false)
    }
  }

  const formatDate = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true })
  }

  const getDocumentPreview = (content: string, maxLength: number = 150) => {
    if (!content.trim()) return 'No content'
    return content.length > maxLength ? content.slice(0, maxLength) + '...' : content
  }

  return (
    <div className="min-h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Documents</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {filteredDocuments.length} {filteredDocuments.length === 1 ? 'document' : 'documents'}
            </p>
          </div>

          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all shadow-md hover:shadow-lg font-medium text-sm sm:text-base"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">New Document</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>

        {/* Filters and Search */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-4">
          {/* View Filter */}
          <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-1 overflow-x-auto scrollbar-hide">
            {[
              { id: 'all' as ViewFilter, label: 'All', icon: DocumentTextIcon, fullLabel: 'All Documents' },
              { id: 'recent' as ViewFilter, label: 'Recent', icon: ClockIcon, fullLabel: 'Recent' },
              { id: 'favorites' as ViewFilter, label: 'Favorites', icon: StarIcon, fullLabel: 'Favorites' },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => handleFilterChange(filter.id)}
                className={clsx(
                  'flex items-center px-3 sm:px-4 py-2 text-sm font-medium rounded-md transition-all whitespace-nowrap flex-shrink-0',
                  viewFilter === filter.id
                    ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                )}
              >
                <filter.icon className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{filter.fullLabel}</span>
                <span className="sm:hidden">{filter.label}</span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 min-w-0 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base"
            />
          </div>

          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base min-w-[140px]"
          >
            <option value="updated">Last Updated</option>
            <option value="created">Date Created</option>
            <option value="title">Title A-Z</option>
            <option value="size">Size</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full" />
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
            <DocumentTextIcon className="w-16 h-16 mb-4" />
            <h3 className="text-lg font-medium mb-2 dark:text-gray-300">
              {searchQuery ? 'No documents found' : `No ${viewFilter} documents`}
            </h3>
            <p className="text-sm">
              {searchQuery 
                ? 'Try adjusting your search terms' 
                : viewFilter === 'all' 
                  ? 'Create your first document to get started'
                  : `No ${viewFilter} documents yet`
              }
            </p>
            {!searchQuery && viewFilter === 'all' && (
              <button
                onClick={() => setCreateModalOpen(true)}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Create Document
              </button>
            )}
          </div>
        ) : (
          <div className="p-4 sm:p-6 overflow-y-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
              <AnimatePresence>
                {filteredDocuments.map((document) => (
                  <motion.div
                    key={document.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer group border border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600"
                    onClick={() => handleOpenDocument(document)}
                  >
                    <div className="p-4 sm:p-6">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3 gap-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate flex-1 text-base sm:text-lg">
                          {document.title}
                        </h3>
                        <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setSelectedDocumentForShare(document.id)
                              setShareModalOpen(true)
                            }}
                            className="p-1.5 sm:p-1 text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            title="Share document"
                          >
                            <ShareIcon className="w-4 h-4 sm:w-4" />
                          </button>
                          <button
                            onClick={(e) => handleToggleFavorite(document, e)}
                            className="p-1.5 sm:p-1 text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-400 transition-colors rounded-md hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                            title={document.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            {document.is_favorite ? (
                              <StarIconSolid className="w-4 h-4 sm:w-4 text-yellow-500" />
                            ) : (
                              <StarIcon className="w-4 h-4 sm:w-4" />
                            )}
                          </button>
                          <button
                            onClick={(e) => handleDeleteDocument(document, e)}
                            className="p-1.5 sm:p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Delete document"
                          >
                            <TrashIcon className="w-4 h-4 sm:w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Preview */}
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                        {getDocumentPreview(document.content)}
                      </p>

                      {/* Tags */}
                      <div className="mb-4" onClick={(e) => e.stopPropagation()}>
                        {editingTagsFor === document.id ? (
                          <TagEditor
                            documentId={document.id}
                            currentTags={document.tags || []}
                            onClose={() => setEditingTagsFor(null)}
                          />
                        ) : (
                          <div className="flex items-center gap-2 flex-wrap">
                            {document.tags && document.tags.length > 0 ? (
                              document.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded text-xs"
                                >
                                  {tag}
                                </span>
                              ))
                            ) : (
                              <span className="text-xs text-gray-400 dark:text-gray-500">No tags</span>
                            )}
                            {document.tags && document.tags.length > 3 && (
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                +{document.tags.length - 3} more
                              </span>
                            )}
                            <button
                              onClick={() => setEditingTagsFor(document.id)}
                              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {document.tags && document.tags.length > 0 ? 'Edit' : 'Add tags'}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-3">
                          <span>{document.word_count} words</span>
                          <span>{Math.ceil(document.character_count / 1000)}k chars</span>
                        </div>
                        <span title={new Date(document.updated_at).toLocaleString()}>
                          {formatDate(document.updated_at)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {selectedDocumentForShare && (
        <ShareDocumentModal
          documentId={selectedDocumentForShare}
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false)
            setSelectedDocumentForShare(null)
          }}
          onShareUpdate={() => {
            // Refresh documents list
            // React Query will automatically refetch
          }}
        />
      )}

      {/* Create Document Modal */}
      <CreateDocumentModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreateDocument}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setDocumentToDelete(null)
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Document"
        message={documentToDelete ? `Are you sure you want to delete "${documentToDelete.title}"? This action cannot be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  )
}

export default DocumentsPage